import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import JobAddBar from "../components/timesheets/JobAddBar";
import Container from "../components/library/Container";
import MonthSelect from "../components/timesheets/MonthSelect";
import Calendar from "../components/timesheets/Calendar";
import TaskList, { HOLIDAY, SICK } from "../components/timesheets/TaskList";
import Timer from "../components/timesheets/Timer";
import { Redirect, useLocation, useParams } from "react-router-dom";

const notCopyableTasks = [HOLIDAY, SICK];

function Timesheets({ requiredMinutes, initialDate, name }) {
  const [selectedDate, setSelectedDate] = useState(
    moment(initialDate || new Date())
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss")
  );
  const [days, setDays] = useState([]);
  const [timerTasks, setTimerTasks] = useState([]);
  const [cacheBuster, setCacheBuster] = useState(0);
  const [approved, setApproved] = useState(null);
  const [isDayApproved, setIsDayApproved] = useState(false);

  const selectedMonthStart = moment(selectedDate).format("YYYY-MM-01 00:00:00");

  const { userId } = useParams();
  const { pathname } = useLocation();

  const fetchDays = useCallback(() => {
    return fetch(
      `${
        process.env.REACT_APP_API_ENDPOINT
      }/api/days?startMonth=${encodeURIComponent(
        moment(selectedDate).startOf("month").format("YYYY-MM-DD HH:mm:ss")
      )}&endMonth=${moment(selectedDate)
        .endOf("month")
        .format("YYYY-MM-DD HH:mm:ss")}${userId ? `&userId=${userId}` : ""}`,
      {
        headers: {
          "x-access-token": localStorage.getItem("access_token"),
        },
      }
    )
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        }
        throw new Error(await res.text());
      })
      .then((response) => {
        return setDays(response);
      })
      .catch((error) => {
        console.error(error);
        window.location = `${process.env.REACT_APP_AUTH_ENDPOINT}/logout/${process.env.REACT_APP_JWT_CLIENT_ID}`;
      });
  }, [selectedDate]);

  const fetchApproved = async () => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_ENDPOINT
        }/api/approve?startMonth=${encodeURIComponent(
          moment(selectedDate).startOf("month").format("YYYY-MM-DD HH:mm:ss")
        )}&endMonth=${moment(selectedDate)
          .endOf("month")
          .format("YYYY-MM-DD HH:mm:ss")}&isManager=${
          userId ? "true" : "false"
        }${userId ? `&approvedFor=${userId}` : ""}`,
        { headers: { "x-access-token": localStorage.getItem("access_token") } }
      );

      const newApproved = await response.json();

      setApproved(newApproved);
    } catch (err) {
      setApproved([]);
      console.error(err);
    }
  };

  const onDateSelect = (newDate) => {
    const newMonthStart = moment(newDate).format("YYYY-MM-01 00:00:00");
    if (newMonthStart !== selectedMonthStart) {
      setDays([]);
    }
    setSelectedDate(newDate);
  };

  const onAddJob = useCallback(
    (jobId, job, useFavourite, minutes = 0) => {
      fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/api/days${
          userId ? `?userId=${userId}` : ""
        }`,
        {
          headers: {
            "x-access-token": localStorage.getItem("access_token"),
            "content-type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            job: jobId,
            name: job.jobId ? `${job.jobId}: ${job.name}` : job.name,
            date: moment(selectedDate).format("YYYY-MM-DD"),
            minutes: minutes || (useFavourite && job.favouriteMinutes) || 0,
          }),
        }
      )
        .then((res) => {
          if (!res.ok) {
            throw res;
          }
          return res.json();
        })
        .then(fetchDays)
        .catch((error) => console.error(error));
    },
    [fetchDays, selectedDate]
  );

  const onRemoveTask = (id) => {
    return fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/api/days/${id}${
        userId ? `?userId=${userId}` : ""
      }`,
      {
        headers: {
          "x-access-token": localStorage.getItem("access_token"),
          "content-type": "application/json",
        },
        method: "DELETE",
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(fetchDays)
      .catch((error) => console.error(error));
  };

  const onAddFavourite = (jobId, minutes) => {
    fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/favourites`, {
      headers: {
        "x-access-token": localStorage.getItem("access_token"),
        "content-type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        jobId,
        minutes: minutes || 0,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(fetchDays)
      .catch((error) => console.error(error));
  };

  const onRemoveFavourite = (jobId) => {
    return fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/api/favourites/${jobId}`,
      {
        headers: {
          "x-access-token": localStorage.getItem("access_token"),
          "content-type": "application/json",
        },
        method: "DELETE",
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(fetchDays)
      .catch((error) => console.error(error));
  };

  const onUpdateTaskMinutes = (id, newMinutes) => {
    return fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/api/days/${id}${
        userId ? `?userId=${userId}` : ""
      }`,
      {
        headers: {
          "x-access-token": localStorage.getItem("access_token"),
          "content-type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          minutes: newMinutes,
        }),
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(fetchDays)
      .catch((error) => console.error(error));
  };

  const fetchTimerTasks = () => {
    return fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/activity`, {
      headers: {
        "x-access-token": localStorage.getItem("access_token"),
        "content-type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(setTimerTasks)
      .catch((error) => console.error(error));
  };

  const onStartTimer = (id, jobId, name) => {
    return fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/activate`, {
      headers: {
        "x-access-token": localStorage.getItem("access_token"),
        "content-type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        id,
        job: jobId,
        name: name,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(fetchTimerTasks)
      .catch((error) => console.error(error));
  };

  const onStopTimer = () => {
    return fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/activate`, {
      headers: {
        "x-access-token": localStorage.getItem("access_token"),
        "content-type": "application/json",
      },
      method: "DELETE",
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(() => {
        setTimerTasks([]);
      })
      .then(fetchDays)
      .then(() => {
        setCacheBuster(Math.random());
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    fetchDays();
    fetchApproved();
  }, [selectedDate]);
  useEffect(() => {
    fetchTimerTasks();
  }, []);

  const selectDayTasks = days.filter(
    (f) =>
      moment(f.date).utc().format("YYYY-MM-DD") ===
      moment(selectedDate).format("YYYY-MM-DD")
  );

  const onCopyDay = useCallback(
    (e) => {
      const copyableTasks = selectDayTasks.filter((task) => {
        // Remove holidays and sickness before adding to clipboard

        return notCopyableTasks.indexOf(task.job) === -1;
      });

      localStorage.setItem("clipboard", JSON.stringify(copyableTasks));
    },
    [selectDayTasks]
  );

  const onPasteDay = useCallback(
    (e) => {
      e.preventDefault();
      const dataString = localStorage.getItem("clipboard");
      try {
        const data = JSON.parse(dataString);
        for (const row of data) {
          onAddJob(row.job, row, false, row.minutes);
        }
      } catch (e) {
        console.error("Invalid clipboard data");
      }
    },
    [onAddJob]
  );

  useEffect(() => {
    document.addEventListener("copy", onCopyDay);
    document.addEventListener("paste", onPasteDay);
    return () => {
      document.removeEventListener("copy", onCopyDay);
      document.removeEventListener("paste", onPasteDay);
    };
  });

  useEffect(() => {
    if (!approved) return;
    setIsDayApproved(
      approved.findIndex(
        (day) =>
          moment(day.date).format("YYYY-MM-DD") ===
          moment(selectedDate).format("YYYY-MM-DD")
      ) > -1
    );
  }, [approved]);

  return (
    <>
      {pathname.includes("users") && !userId && <Redirect to="/" />}
      <JobAddBar
        onAddJob={onAddJob}
        onCopyDay={onCopyDay}
        onPasteDay={onPasteDay}
        userId={userId}
        name={name}
        isApproved={isDayApproved}
      />
      <Container style={{ flexGrow: 1, background: "var(--grey-110-color)" }}>
        <div className="App__Columns">
          <div className="App__LeftColumn">
            {timerTasks.length > 0 && (
              <Timer task={timerTasks[0]} onStopTimer={onStopTimer} />
            )}
            <h1>
              {moment(selectedDate).calendar({
                sameDay: "[Today]",
                nextDay: "[Tomorrow]",
                nextWeek: "dddd",
                lastDay: "[Yesterday]",
                lastWeek: "[Last] dddd",
                sameElse: "DD/MM/YYYY",
              })}
              {isDayApproved && " - Approved"}
            </h1>
            <TaskList
              key={cacheBuster}
              tasks={selectDayTasks}
              isApproved={isDayApproved}
              trackingTask={timerTasks[0]}
              onFinishedTaskTimeEdit={fetchDays}
              onUpdateTaskMinutes={onUpdateTaskMinutes}
              onAddFavourite={onAddFavourite}
              onRemoveFavourite={onRemoveFavourite}
              onRemoveTask={onRemoveTask}
              onStartTimer={onStartTimer}
              onStopTimer={onStopTimer}
              requiredMinutes={requiredMinutes}
              userId={userId}
            />
          </div>
          <div className="App__RightColumn">
            <div
              style={{
                background: "#32323b",
                padding: 20,
                borderRadius: 4,
              }}
            >
              <MonthSelect date={selectedDate} onDateSelect={onDateSelect} />
              <Calendar
                approved={approved}
                date={selectedDate}
                days={days}
                onDateSelect={onDateSelect}
                requiredMinutes={requiredMinutes}
              />
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

export default Timesheets;
